/**
 * Financial Reconciliation dashboard — data layer.
 * Every call goes through the shared axios service (Bearer token auto-attached,
 * baseURL = {host}/v1/api). Endpoints are the read-only /finrecon/* routes.
 */
import type { ApiResponse } from '../../services/api';
import { apiService } from '../../services/api';
import type {
	ReconSummary,
	VendorRow,
	VendorDetail,
	ArInvoice,
	RequestActivity,
	RazorpayBridge,
	BankCoverage,
	Attribution,
	Reimbursements,
	UnappliedPayments,
	ReconPagination,
	ReconStatus,
	BankCrosscheck,
	GstSummary,
	GstMeta,
	GstPagination,
	GstTotals,
	GstCategoryCount,
	GstFindingStatus,
	GstFindingUpdateResult,
	GstVendor,
	GstVendorHeader,
	GstVendorRows,
	GstPurchaseRow,
	GstSalesRow,
	GstPurchaseDetail,
	GstSalesDetail,
	StatutorySummary,
	StatutoryFinding,
	StatutoryFindingDetail,
	StatutoryLedgerRow,
	StatutoryStatus,
	StatutoryUpdateResult,
	StatutoryClose,
	StatutoryMatrix,
} from './types';

type Paged<T> = { data: T; pagination: ReconPagination };

async function get<T>(path: string, timeout?: number): Promise<ApiResponse<T>> {
	return (await apiService.get(path, timeout ? { timeout } : undefined)) as ApiResponse<T>;
}

export async function getSummary(): Promise<ReconSummary> {
	return (await get<ReconSummary>('/finrecon/summary')).data;
}

export async function getVendors(params: {
	bucket?: string;
	flag?: string;
	search?: string;
	from?: string;
	to?: string;
	sortBy?: string;
	order?: string;
	limit?: number;
	offset?: number;
}): Promise<Paged<VendorRow[]>> {
	const q = new URLSearchParams();
	if (params.bucket) q.set('bucket', params.bucket);
	if (params.flag) q.set('flag', params.flag);
	if (params.search) q.set('search', params.search);
	if (params.from) q.set('from', params.from);
	if (params.to) q.set('to', params.to);
	if (params.sortBy) q.set('sortBy', params.sortBy);
	if (params.order) q.set('order', params.order);
	q.set('limit', String(params.limit ?? 25));
	q.set('offset', String(params.offset ?? 0));
	const res = await get<VendorRow[]>(`/finrecon/vendors?${q.toString()}`);
	return { data: res.data, pagination: (res as unknown as Paged<VendorRow[]>).pagination };
}

export async function getReconStatus(): Promise<ReconStatus> {
	return (await get<ReconStatus>('/finrecon/reconciliation-status')).data;
}

/** All vendors (matched + gaps) so the view can tab client-side without refetching. */
export async function getBankCrosscheck(limit = 1000): Promise<BankCrosscheck> {
	const res = (await apiService.get(
		`/finrecon/vendors/bank-crosscheck?onlyGaps=false&limit=${limit}`
	)) as ApiResponse<BankCrosscheck['vendors']> & {
		summary: BankCrosscheck['summary'];
		pagination: ReconPagination;
		note: string;
	};
	return { summary: res.summary, vendors: res.data, pagination: res.pagination, note: res.note };
}

export async function getVendorDetail(
	vendorNorm: string,
	params: { from?: string; to?: string } = {}
): Promise<VendorDetail> {
	const q = new URLSearchParams();
	if (params.from) q.set('from', params.from);
	if (params.to) q.set('to', params.to);
	const qs = q.toString();
	return (
		await get<VendorDetail>(`/finrecon/vendors/${encodeURIComponent(vendorNorm)}${qs ? `?${qs}` : ''}`)
	).data;
}

/** Set / clear a vendor's manual review flag. status 'clear' removes it. */
export async function setVendorReview(
	vendorNorm: string,
	status: 'needs_review' | 'reviewed_ok' | 'clear',
	note?: string
): Promise<{ vendor_norm: string; review_status: 'needs_review' | 'reviewed_ok' | null }> {
	const res = (await apiService.post(`/finrecon/vendors/${encodeURIComponent(vendorNorm)}/review`, {
		status,
		note,
	})) as ApiResponse<{ vendor_norm: string; review_status: 'needs_review' | 'reviewed_ok' | null }>;
	return res.data;
}

export async function getArInvoices(params: {
	onlyUnlinked?: boolean;
	status?: string;
	limit?: number;
	offset?: number;
}): Promise<Paged<ArInvoice[]>> {
	const q = new URLSearchParams();
	if (params.onlyUnlinked) q.set('onlyUnlinked', 'true');
	if (params.status) q.set('status', params.status);
	q.set('limit', String(params.limit ?? 25));
	q.set('offset', String(params.offset ?? 0));
	const res = await get<ArInvoice[]>(`/finrecon/ar-invoices?${q.toString()}`);
	return { data: res.data, pagination: (res as unknown as Paged<ArInvoice[]>).pagination };
}

export async function getRequestActivity(): Promise<RequestActivity> {
	return (await get<RequestActivity>('/finrecon/requests')).data;
}

export async function getRazorpayBridge(): Promise<RazorpayBridge> {
	return (await get<RazorpayBridge>('/finrecon/bank/razorpay-bridge')).data;
}

export async function getBankCoverage(): Promise<BankCoverage> {
	return (await get<BankCoverage>('/finrecon/bank/coverage')).data;
}

export async function getAttribution(): Promise<Attribution> {
	return (await get<Attribution>('/finrecon/attribution')).data;
}

export async function getReimbursements(): Promise<Reimbursements> {
	return (await get<Reimbursements>('/finrecon/reimbursements')).data;
}

export async function getUnappliedPayments(): Promise<UnappliedPayments> {
	return (await get<UnappliedPayments>('/finrecon/unapplied-payments')).data;
}

/** `totals` is a sibling of `data`/`pagination` in the envelope, not nested inside pagination.
 *  Optional so an API predating it degrades to "no money line" rather than crashing. */
type GstPaged<T> = {
	data: T;
	pagination: GstPagination;
	totals?: GstTotals;
	/** Purchases only: the cause breakdown of the current filtered view. */
	by_category?: GstCategoryCount[];
};

/** Must be called with exactly the same filters as the row lists, otherwise the tab counts
 *  describe a different set than the table underneath them — the full year over one month's
 *  rows, or every vendor over one vendor's rows. */
export async function getGstSummary(
	params: { period?: string; vendor?: string } = {}
): Promise<GstSummary> {
	const q = new URLSearchParams();
	if (params.period) q.set('period', params.period);
	if (params.vendor) q.set('vendor', params.vendor);
	const qs = q.toString();
	return (await get<GstSummary>(`/gst-recon/summary${qs ? `?${qs}` : ''}`)).data;
}

/** Vendor picker options. Narrows with the same `period` the rest of the screen is on, and
 *  `side` decides whether these are suppliers (purchases) or customers (sales). */
export async function getGstVendors(params: {
	side: 'purchases' | 'sales';
	period?: string;
}): Promise<GstVendor[]> {
	const q = new URLSearchParams();
	if (params.side === 'sales') q.set('side', 'sales');
	if (params.period) q.set('period', params.period);
	const qs = q.toString();
	return (await get<GstVendor[]>(`/gst-recon/vendors${qs ? `?${qs}` : ''}`)).data;
}

/** Vendor-level drill-down: the three headline figures plus the tab list. The tabs come from
 *  the API, never hardcoded, exactly as the status tabs come from /summary.
 *  Only meaningful for vendors with a real GSTIN — the route validates 15 characters, so the
 *  199 name-only vendors 400 here and must not be offered this view. */
export async function getGstVendorHeader(
	gstin: string,
	params: { period?: string } = {}
): Promise<GstVendorHeader> {
	const q = new URLSearchParams();
	if (params.period) q.set('period', params.period);
	const qs = q.toString();
	return (
		await get<GstVendorHeader>(
			`/gst-recon/vendors/${encodeURIComponent(gstin)}/header${qs ? `?${qs}` : ''}`
		)
	).data;
}

/**
 * One tab's rows. The tab is the URL — /bank, /zoho, /ledger, /trace — not a `?tab=` parameter,
 * so each response shape has its own address. Shapes differ per tab, so the caller renders
 * columns from the keys it knows.
 *
 * `statement` applies to /ledger only: vendor statements overlap, and summing them would
 * double-count, so the API serves the most recent one covering the window and lists the rest.
 */
export async function getGstVendorRows(
	gstin: string,
	params: {
		tab: string;
		period?: string;
		page?: number;
		pageSize?: number | 'all';
		statement?: string;
	}
): Promise<GstVendorRows> {
	const q = new URLSearchParams();
	if (params.period) q.set('period', params.period);
	if (params.statement) q.set('statement', params.statement);
	q.set('page', String(params.page ?? 1));
	q.set('pageSize', String(params.pageSize ?? 50));
	const res = (await apiService.get(
		`/gst-recon/vendors/${encodeURIComponent(gstin)}/${encodeURIComponent(
			params.tab
		)}?${q.toString()}`
	)) as ApiResponse<GstVendorRows['data']> & Omit<GstVendorRows, 'data'>;
	return {
		data: res.data,
		pagination: res.pagination,
		tab: res.tab,
		note: res.note,
		totals: res.totals,
		statement: res.statement,
		statements_available: res.statements_available,
		by_month: res.by_month,
		traced_count: res.traced_count,
		traced_value: res.traced_value,
		months_covered: res.months_covered,
		months_short: res.months_short,
	};
}

/**
 * Set the review status on one or more findings. The only write on this screen.
 *
 * Two axes, and both can travel in one call: `ids` are FINDING ids (the whole vendor's ruling),
 * `rows` are row ids from /purchases (one invoice each). At least one is required.
 *
 * A vendor-level `reviewed_ok` is REFUSED while any of its money is still unexplained — the
 * server writes nothing and returns the vendor in `refused`. Signing off that vendor's explained
 * invoices individually via `rows` is the way through.
 *
 * `note` is APPENDED to the finding's note server-side, never overwriting it.
 *
 * Signing off removes only the invoices already marked explained. One still marked open stays in
 * the gap afterwards — correct where the bill was found but the GSTIN is still wrong. Approving
 * states something about the finding, it is not a write-off, so the UI must not imply the gap
 * cleared just because the call succeeded: read `liable_rs` on the way back.
 */
export async function updateGstFindings(
	sel: { ids?: number[]; rows?: number[] },
	/** null when applying an action alone — the server derives the status from the action, and
	 *  sending one anyway would overstate what the UI decided. At least one of status/action_code
	 *  is required. */
	status: GstFindingStatus | null,
	note?: string,
	actionCode?: string
): Promise<GstFindingUpdateResult> {
	/* The result lives INSIDE `data` — unlike the read endpoints, where `pagination` and `totals`
	   sit beside it at the top level. Reading it from the top level silently yields undefined,
	   which coerces to an empty array and reports "nothing was updated" for a write that
	   succeeded. */
	const body = {
		...(sel.ids?.length ? { ids: sel.ids } : {}),
		...(sel.rows?.length ? { rows: sel.rows } : {}),
		...(status ? { status } : {}),
		...(actionCode ? { action_code: actionCode } : {}),
		...(note ? { note } : {}),
	};
	const res = (await apiService.patch(
		'/gst-recon/findings',
		body
	)) as ApiResponse<GstFindingUpdateResult>;
	const d = res.data;
	return {
		updated: d?.updated ?? [],
		updated_rows: d?.updated_rows ?? [],
		refused: d?.refused ?? [],
		refused_rows: d?.refused_rows ?? [],
		not_found: d?.not_found ?? [],
		gap_now: d?.gap_now,
		performed_by: d?.performed_by,
	};
}

export async function getGstMeta(): Promise<GstMeta> {
	return (await get<GstMeta>('/gst-recon/meta')).data;
}

interface GstRowParams {
	status?: string;
	search?: string;
	/** 'YYYY-MM' — the GSTR-2B return month the line arrived in, not the invoice's own date.
	 *  The two differ for quarterly (QRMP) and late filers, so this is the ITC-claim axis. */
	period?: string;
	/** GSTIN when the vendor has one, else the vendor name — post back `GstVendor.value`
	 *  verbatim; never rebuild it. */
	vendor?: string;
	page?: number;
	/** a number, or the literal string 'all' to bypass pagination entirely. */
	pageSize?: number | 'all';
	/** The investigation's cause. 'none' selects the rows nobody has investigated yet — it has to
	 *  be an explicit value because "no category" cannot be typed as a category. */
	category?: string;
	finding_status?: string;
}

function gstQuery(params: GstRowParams): string {
	const q = new URLSearchParams();
	if (params.status) q.set('status', params.status);
	if (params.search) q.set('search', params.search);
	if (params.period) q.set('period', params.period);
	if (params.vendor) q.set('vendor', params.vendor);
	if (params.category) q.set('category', params.category);
	if (params.finding_status) q.set('finding_status', params.finding_status);
	q.set('page', String(params.page ?? 1));
	q.set('pageSize', String(params.pageSize ?? 50));
	return q.toString();
}

export async function getGstPurchases(params: GstRowParams): Promise<GstPaged<GstPurchaseRow[]>> {
	const res = await get<GstPurchaseRow[]>(`/gst-recon/purchases?${gstQuery(params)}`);
	const env = res as unknown as GstPaged<GstPurchaseRow[]> & { by_category?: GstCategoryCount[] };
	return { data: res.data, pagination: env.pagination, totals: env.totals, by_category: env.by_category };
}

export async function getGstSales(params: GstRowParams): Promise<GstPaged<GstSalesRow[]>> {
	const res = await get<GstSalesRow[]>(`/gst-recon/sales?${gstQuery(params)}`);
	const env = res as unknown as GstPaged<GstSalesRow[]>;
	return { data: res.data, pagination: env.pagination, totals: env.totals };
}

export async function getGstPurchaseDetail(id: number): Promise<GstPurchaseDetail> {
	return (await get<GstPurchaseDetail>(`/gst-recon/purchases/${id}`)).data;
}

export async function getGstSalesDetail(id: number): Promise<GstSalesDetail> {
	return (await get<GstSalesDetail>(`/gst-recon/sales/${id}`)).data;
}

export type GstAttachment = { kind: 'pdf'; blobUrl: string } | { kind: 'none'; note: string };

/**
 * The attachment endpoint returns either raw PDF bytes or a small JSON "nothing here" body on
 * the same 200 status — Content-Type is the only way to tell them apart. Always fetched as a
 * blob so the JSON branch can still be read back out via `.text()`.
 */
async function fetchGstAttachment(url: string): Promise<GstAttachment> {
	const blob = (await apiService.get(url, { responseType: 'blob' })) as unknown as Blob;
	if (blob.type.includes('application/json')) {
		const parsed = JSON.parse(await blob.text());
		return { kind: 'none', note: parsed?.data?.note || parsed?.message || 'No document on file.' };
	}
	return { kind: 'pdf', blobUrl: URL.createObjectURL(blob) };
}

/** Fetches live from Zoho on first call only (server-side cached after) — never pre-fetch in bulk. */
export async function getGstPurchaseAttachment(id: number): Promise<GstAttachment> {
	return fetchGstAttachment(`/gst-recon/purchases/${id}/attachment`);
}

export async function getGstSalesAttachment(id: number): Promise<GstAttachment> {
	return fetchGstAttachment(`/gst-recon/sales/${id}/attachment`);
}

/** Filters that narrow the export belong in its name — download the same bucket for two causes
 *  and otherwise you get two files called the same thing, one silently overwriting the other. */
const csvSuffix = (p: { period?: string; category?: string }) =>
	(p.period ? `_${p.period}` : '') + (p.category ? `_${p.category}` : '');

async function downloadGstCsv(
	url: string,
	/* Every filter the screen can apply has to be listed here. The callers' own signatures are
	   not enough: an extra key in `params` that this function does not name is dropped silently,
	   which is how "Export CSV" came to ignore the selected cause and hand back all 1,428 rows
	   while the table showed 33. */
	params: {
		status?: string;
		search?: string;
		period?: string;
		vendor?: string;
		category?: string;
		finding_status?: string;
	},
	filename: string
): Promise<void> {
	const q = new URLSearchParams();
	if (params.status) q.set('status', params.status);
	if (params.search) q.set('search', params.search);
	if (params.period) q.set('period', params.period);
	if (params.vendor) q.set('vendor', params.vendor);
	if (params.category) q.set('category', params.category);
	if (params.finding_status) q.set('finding_status', params.finding_status);
	const qs = q.toString();
	const blob = (await apiService.get(`${url}${qs ? `?${qs}` : ''}`, {
		responseType: 'blob',
	})) as unknown as Blob;
	// The backend CSV has no BOM — without it, Excel/Numbers can misdetect the encoding
	// and mis-render rows containing multi-byte characters (the reason column's em-dash),
	// the exact "columns look blank" symptom seen here. Every other export in this app
	// already prepends one; do the same here rather than relying on the source to.
	const text = await blob.text();
	const withBom = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8;' });
	triggerBlobDownload(withBom, filename);
}

/** Every row for the current filter — no pagination params, always the full filtered set.
 *  The month goes in the filename too: downloading Apr then May must not produce two
 *  identically-named files in the same folder. */
export async function exportGstPurchases(params: {
	status?: string;
	search?: string;
	period?: string;
	vendor?: string;
	category?: string;
	finding_status?: string;
}): Promise<void> {
	return downloadGstCsv(
		'/gst-recon/purchases/export',
		params,
		`gst_purchases_2b_vs_zoho${csvSuffix(params)}.csv`
	);
}

export async function exportGstSales(params: {
	status?: string;
	search?: string;
	period?: string;
	vendor?: string;
}): Promise<void> {
	return downloadGstCsv(
		'/gst-recon/sales/export',
		params,
		`gst_sales_gstr1_vs_zoho${csvSuffix(params)}.csv`
	);
}

export interface UploadResult {
	account_number: string;
	detected_columns: Record<string, string | null>;
	rows_in_file: number;
	rows_loaded: number;
	rows_skipped: number;
	date_range: { from: string; to: string };
	withdrawn: number;
	deposited: number;
	new_payments_verified: number;
	note: string;
}

/** POST a bank/card statement (CSV or Excel). Matching runs server-side on upload. */
export async function uploadBankStatement(
	file: File,
	accountNumber: string,
	bank?: string
): Promise<UploadResult> {
	const form = new FormData();
	form.append('file', file);
	form.append('account_number', accountNumber);
	if (bank) form.append('bank', bank);
	const res = (await apiService.post('/finrecon/bank/upload', form, {
		headers: { 'Content-Type': 'multipart/form-data' },
	})) as ApiResponse<UploadResult>;
	return res.data;
}

/**
 * Trigger a real file download with a stable filename.
 * Uses a blob object URL + MouseEvent click (FileSaver pattern). Avoid data: URIs —
 * Chrome often saves those as extensionless UUIDs, which then can't Open / Show in Finder.
 * Delay revoke so the browser finishes reading the blob before the URL is torn down.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.rel = 'noopener';
	a.style.display = 'none';
	document.body.appendChild(a);
	a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
	setTimeout(() => {
		a.remove();
		URL.revokeObjectURL(url);
	}, 1000);
}

/**
 * Minimal RFC-4180 CSV download — no dependency. Numbers stay raw (unformatted)
 * so the file is sum/pivot-ready in Excel; the escaper guards commas/quotes/newlines
 * in vendor names so a stray comma can't shift every column right.
 */
export function downloadCsv(
	filename: string,
	headers: string[],
	rows: Array<Array<string | number | null | undefined>>
): void {
	const esc = (v: string | number | null | undefined) => {
		const s = v == null ? '' : String(v);
		return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
	};
	const csv = [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
	// BOM so Excel opens rupee symbols correctly
	const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
	triggerBlobDownload(blob, filename);
}

/** Compact INR formatter used across the reconciliation views. */
export function inr(n: number | null | undefined): string {
	const v = Number(n || 0);
	if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
	if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
	if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
	return `₹${v.toFixed(0)}`;
}

export function inrFull(n: number | null | undefined): string {
	return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

/* ── Statutory reconciliation ────────────────────────────────────────────────────────────────
 * /statutory-recon/* — findings over PF, PF EDLI/Admin, ESIC and PT, plus the stored Zoho leg.
 * Same shared axios service, same Bearer token, same {host}/v1/api base as everything above. */

// ponytail: summary (~6s) and matrix (~11s) on stage outrun the 10s default; speed up the backend to drop this.
const STATUTORY_TIMEOUT = 30000;

/** Header numbers. `legs_not_built` comes back with it and must be shown — only 1 of 4 legs
 *  has data, and a screen that hides that reads as "reconciled" when nothing is reconciled. */
export async function getStatutorySummary(): Promise<StatutorySummary> {
	return (await get<StatutorySummary>('/statutory-recon/summary', STATUTORY_TIMEOUT)).data;
}

/**
 * The four-leg matrix: challan vs return vs bank vs Zoho, per head and per month, plus the list
 * of documents we do not hold. This is the view the accounts team works from.
 */
export async function getStatutoryMatrix(fy = '2025-26'): Promise<StatutoryMatrix> {
	return (await get<StatutoryMatrix>(`/statutory-recon/matrix?fy=${encodeURIComponent(fy)}`, STATUTORY_TIMEOUT)).data;
}

/** The worklist. Sorted server-side by liable_rs desc, so the biggest open money is first. */
export async function getStatutoryFindings(
	params: { status?: string; owner?: string; group?: string; q?: string; pageSize?: number } = {}
): Promise<{ rows: StatutoryFinding[]; pagination: ReconPagination }> {
	const p = new URLSearchParams();
	if (params.status) p.set('status', params.status);
	if (params.owner) p.set('owner', params.owner);
	if (params.group) p.set('group', params.group);
	if (params.q) p.set('q', params.q);
	p.set('pageSize', String(params.pageSize ?? 200));
	const res = await get<StatutoryFinding[]>(`/statutory-recon/findings?${p.toString()}`);
	return { rows: res.data ?? [], pagination: (res as unknown as Paged<unknown>).pagination };
}

/** One finding with every evidence line resolved — the "how we cross-checked it" drawer. */
export async function getStatutoryFinding(id: number): Promise<StatutoryFindingDetail> {
	return (await get<StatutoryFindingDetail>(`/statutory-recon/findings/${id}`)).data;
}

/** The Zoho ledger rows behind the numbers. `month` is the PAYMENT month, not the wage month —
 *  May wages are paid in June, and nothing sets wage_month yet. */
export async function getStatutoryLedger(
	params: { line?: string; month?: string; pageSize?: number } = {}
): Promise<{ rows: StatutoryLedgerRow[]; totals: { provisioned_rs: number; paid_rs: number } }> {
	const p = new URLSearchParams();
	if (params.line) p.set('line', params.line);
	if (params.month) p.set('month', params.month);
	p.set('pageSize', String(params.pageSize ?? 500));
	const res = (await apiService.get(
		`/statutory-recon/ledger?${p.toString()}`
	)) as ApiResponse<StatutoryLedgerRow[]> & {
		totals?: { provisioned_rs: number; paid_rs: number };
	};
	return { rows: res.data ?? [], totals: res.totals ?? { provisioned_rs: 0, paid_rs: 0 } };
}

/** Sign-off. `ids` is always an array so single and bulk cannot drift.
 *  A 200 can still mean nothing was written — `refused` carries reviewed_ok attempts blocked by
 *  an open balance, and `not_found` carries stale ids. Read the body, not just the status. */
export async function updateStatutoryFindings(
	ids: number[],
	status: StatutoryStatus,
	note?: string
): Promise<StatutoryUpdateResult> {
	const res = (await apiService.patch('/statutory-recon/findings', {
		ids,
		status,
		...(note ? { note } : {}),
	})) as ApiResponse<StatutoryUpdateResult>;
	const d = res.data;
	return {
		updated: d?.updated ?? [],
		refused: d?.refused ?? [],
		not_found: d?.not_found ?? [],
		open_now: d?.open_now ?? { findings: 0, liable_rs: 0 },
	};
}

/** An evidence attachment, fetched THROUGH the axios service so the Bearer token is attached.
 *
 *  A plain <a href> to /recon/document opens a tab with no Authorization header and 401s — the
 *  proxy is auth-gated. Same reason the GST tab fetches its documents as a blob. */
export async function getStatutoryEvidenceFile(storageKey: string): Promise<string> {
	const blob = (await apiService.get(
		`/recon/document?key=${encodeURIComponent(storageKey)}`,
		{ responseType: 'blob' }
	)) as unknown as Blob;
	return URL.createObjectURL(blob);
}

/** Control accounts + the month x head grid — the view an accountant starts in. */
export async function getStatutoryClose(fy = '2025-26'): Promise<StatutoryClose> {
	return (await get<StatutoryClose>(`/statutory-recon/close?fy=${encodeURIComponent(fy)}`)).data;
}
